import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Modulation Lab v1 lexicon entries — 1 tool-level + 2 sub-mode + 4 techniques
 * + 2 concepts + 2 feedback wrappers + 3 picker labels = 14 entries. Authored
 * by Bowie in the Modulation Lab v1 scope memo; locked verbatim — do not edit
 * without Bowie review.
 *
 * Outbound cross-link entries (`crosslink-modulation-to-*` + inbound
 * `crosslink-cof-to-modulation`) live in `lexicon-crosslink-entries.ts` to
 * keep the cross-link semantics in one place.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §8.9
 * Bowie scope memo: .claude/agent-memory/bowie/project_modulation_lab_v1_scope.md
 */
export const MODULATION_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───── Tool + sub-modes (3) ─────
  {
    id: 'modulation-lab',
    category: 'modulation',
    displayName: 'Modulation Lab',
    displayNameShort: 'Modulate',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'Hear how songs change key — the techniques composers use to move from one key to another.',
      intermediate:
        'Explore and identify modulation techniques: direct, pivot chord, common tone, and parallel-mode shift.',
      advanced:
        'The cross-key dimension — audition and identify modulation techniques between closely-related keys.',
    },
    tagLabel: { beginner: 'key changes', intermediate: 'modulation', advanced: 'cross-key motion' },
  },
  {
    id: 'modulation-explore',
    category: 'modulation',
    displayName: 'Modulation Explore',
    displayNameShort: 'Explore',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Pick a starting key and a technique — hear how the modulation sounds.',
      intermediate:
        'Pick source key, technique, and destination — audition the modulation across three phases.',
      advanced: 'Free-explore modulations across closely-related keys with four canonical techniques.',
    },
    tagLabel: { beginner: 'explore', intermediate: 'free explore', advanced: 'modulation audition' },
  },
  {
    id: 'modulation-quiz',
    category: 'modulation',
    displayName: 'Modulation ID Quiz',
    displayNameShort: 'Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Listen to a key change and pick the technique used.',
      intermediate: 'Identify the modulation technique from a 12-measure audition.',
      advanced:
        'Recognize the four modulation techniques by ear — direct, pivot, common tone, parallel mode.',
    },
    tagLabel: {
      beginner: 'guess the technique',
      intermediate: 'modulation ID',
      advanced: 'modulation recognition',
    },
  },
  // ───── Technique entries (4) ─────
  {
    id: 'modulation-direct',
    category: 'modulation',
    displayName: 'Direct modulation',
    displayNameShort: 'Direct',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'A sudden key change — the new key just appears, often with a lift in energy. Common in pop "last chorus" key changes.',
      intermediate:
        'Direct (chromatic) modulation — the new key arrives via its dominant, with no shared pivot chord. Abrupt by design.',
      advanced:
        'Direct modulation — V/new key appears with no preparation; the listener experiences a sudden tonal shift. Common in pop "truck-driver" key-up shifts.',
    },
    tagLabel: {
      beginner: 'sudden shift',
      intermediate: 'direct change',
      advanced: 'V/new with no pivot',
    },
  },
  {
    id: 'modulation-pivot',
    category: 'modulation',
    displayName: 'Pivot chord',
    displayNameShort: 'Pivot',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'A chord that belongs to BOTH keys — the song uses it as a bridge from one key to the other.',
      intermediate:
        'Pivot chord — a chord shared between source and destination keys, reinterpreted on the modulation. e.g., C is I of C major and IV of G major.',
      advanced:
        'Pivot chord modulation — the pivot is diatonic in both keys; its function is reinterpreted in the destination. The canonical classical modulation technique.',
    },
    tagLabel: {
      beginner: 'shared chord bridge',
      intermediate: 'pivot chord',
      advanced: 'reinterpreted diatonic chord',
    },
  },
  {
    id: 'modulation-common-tone',
    category: 'modulation',
    displayName: 'Common tone',
    displayNameShort: 'Common tone',
    pedagogicalLevel: 3,
    copy: {
      beginner:
        'A single note is held while the chords change around it — that held note bridges the two keys.',
      intermediate:
        'Common-tone modulation — a single pitch sustained across the modulation point; the harmony shifts beneath it.',
      advanced:
        'Common-tone modulation — a single pitch (often the 5th of source becoming 3rd of destination) is held while the harmony reinterprets the tonal center.',
    },
    tagLabel: {
      beginner: 'held note bridge',
      intermediate: 'common tone',
      advanced: 'sustained pitch pivot',
    },
  },
  {
    id: 'modulation-parallel',
    category: 'modulation',
    displayName: 'Parallel mode shift',
    displayNameShort: 'Parallel',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'The song stays on the same starting note but switches between major and minor — same tonic, different mood.',
      intermediate:
        'Parallel mode shift — modulation by changing mode (major ↔ minor) on the same tonic. The 3rd degree shifts by a chromatic semitone (E → E♭ for C major → C minor, or C → C♯ for A minor → A major).',
      advanced:
        'Parallel modulation — tonic unchanged, mode flipped. Common in film music and jazz; technically a mode change rather than a key change, but functions modulatorily.',
    },
    tagLabel: {
      beginner: 'major/minor flip',
      intermediate: 'parallel mode',
      advanced: 'mode shift on tonic',
    },
  },
  {
    id: 'modulation-deceptive',
    category: 'modulation',
    displayName: 'Deceptive modulation',
    displayNameShort: 'Deceptive',
    pedagogicalLevel: 3,
    copy: {
      beginner:
        'When the dominant chord resolves to an unexpected chord that becomes the new key’s home.',
      intermediate:
        'Deceptive modulation — V of source resolves to vi (instead of I), and vi becomes the new tonic.',
      advanced:
        'Deceptive modulation — V→vi substitutes for V→I; the vi is reinterpreted as i of the relative minor, completing the modulation through a single chord substitution.',
    },
    tagLabel: {
      beginner: 'twist resolution',
      intermediate: 'deceptive cadence',
      advanced: 'V→vi reinterpretation',
    },
  },
  // ───── Concept entries (2) ─────
  {
    id: 'modulation-source-destination',
    category: 'modulation',
    displayName: 'Source and destination keys',
    displayNameShort: 'Source / Dest',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Every modulation has a starting key (source) and an ending key (destination).',
      intermediate:
        'Source = the key you start in. Destination = the key you arrive at. Modulation is the transition between them.',
      advanced:
        'Modulation is defined by source and destination tonal centers, with a modulation phase (one or more measures) bridging them.',
    },
    tagLabel: {
      beginner: 'start + end',
      intermediate: 'source + destination',
      advanced: 'tonal centers',
    },
  },
  {
    id: 'modulation-closely-related',
    category: 'modulation',
    displayName: 'Closely-related modulation',
    displayNameShort: 'Closely related',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The smoothest key changes go to nearby keys — keys that share most of their notes.',
      intermediate:
        'Closely-related modulations move to keys differing by ≤1 sharp/flat — these feel smooth because the source and destination share most diatonic chords.',
      advanced:
        'Closely-related modulation — destinations in the 5-key cluster (IV/V/vi/ii/iii relative). Smoothest motion because of shared diatonic content.',
    },
    tagLabel: {
      beginner: 'nearby keys',
      intermediate: 'closely related',
      advanced: '±1 sharp/flat cluster',
    },
  },
  // ───── Feedback wrappers (2) ─────
  {
    id: 'modulation-explore-feedback',
    category: 'modulation',
    displayName: 'Explore feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Try the same source key with different techniques — hear how each one sounds.',
      intermediate:
        'Compare techniques: pivot feels smooth (shared chord), direct feels sudden (no preparation), common tone feels suspended.',
      advanced:
        'Same source + destination + different technique = different perceptual experience. Pivot is gradual; direct is dramatic; common tone is meditative; parallel is mood-shifting.',
    },
    tagLabel: {
      beginner: 'try the same key',
      intermediate: 'technique comparison',
      advanced: 'perceptual contrast',
    },
  },
  {
    id: 'modulation-quiz-feedback',
    category: 'modulation',
    displayName: 'Quiz feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'Listen for: a sudden lift (direct), a shared chord (pivot), a held note (common tone), or same starting note but minor (parallel).',
      intermediate:
        'Direct = abrupt V/new. Pivot = chord that fits both keys. Common tone = held note across the change. Parallel = tonic unchanged, mode flipped.',
    advanced:
        'Technique discrimination hinges on the MODULATION PHASE shape — sudden V (direct), reinterpreted shared chord (pivot), sustained pitch (common tone), mode flip on tonic (parallel).',
    },
    tagLabel: {
      beginner: 'listen for the bridge',
      intermediate: 'technique signature',
      advanced: 'modulation-phase shape',
    },
  },
  // ───── Source/destination/technique picker labels (3) ─────
  {
    id: 'modulation-source-picker',
    category: 'modulation',
    displayName: 'Starting key',
    displayNameShort: 'From',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The key you start in.',
      intermediate: 'Source key — where the modulation begins.',
      advanced: 'Source tonal center.',
    },
    tagLabel: { beginner: 'start', intermediate: 'source', advanced: 'source tonal center' },
  },
  {
    id: 'modulation-destination-picker',
    category: 'modulation',
    displayName: 'Ending key',
    displayNameShort: 'To',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The key you end up in.',
      intermediate: 'Destination key — where the modulation arrives.',
      advanced: 'Destination tonal center.',
    },
    tagLabel: { beginner: 'end', intermediate: 'destination', advanced: 'destination tonal center' },
  },
  {
    id: 'modulation-technique-picker',
    category: 'modulation',
    displayName: 'Modulation technique',
    displayNameShort: 'Technique',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Pick how the song moves from one key to the other.',
      intermediate: 'Choose a modulation technique to audition.',
      advanced: 'Select the modulation technique for this audition.',
    },
    tagLabel: { beginner: 'how to move', intermediate: 'technique', advanced: 'modulation technique' },
  },
  // ───── v1.5 difficulty picker (1) ─────
  {
    id: 'modulation-difficulty-picker',
    category: 'modulation',
    displayName: 'Difficulty',
    displayNameShort: 'Difficulty',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Easy: only pick the technique. Hard: also pick the starting key.',
      intermediate: 'Easy mode tests technique only. Hard mode tests technique + source-key identification.',
      advanced:
        'Difficulty toggle — easy quizzes technique only; hard adds source-key identification as a second answer phase.',
    },
    tagLabel: { beginner: 'how hard', intermediate: 'difficulty', advanced: 'difficulty mode' },
  },
]
